import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { type FinancialAccount } from '../../hooks/queries/useFinancialAccounts';
import { useDeviceBreakpoints } from '../../hooks/useDeviceBreakpoints';
import { accountAsOf } from '../../utils/account/accounts';
import { sectionDisplayName, sectionTotalTone, type AccountSection } from '../../utils/account/accountSections';
import { likeForLikeTotals } from '../../utils/account/balanceHistory';
import { toCurrency } from '../../utils/formatting';
import AccountRow from '../AccountRow';
import BalanceChangeIndicator from '../changeIndicators/BalanceChangeIndicator';
import ChangeIndicator from '../changeIndicators/ChangeIndicator';
import { getPresetDateRange } from '../DateRangePicker';
import Widget from '../Widget';

import type { ColumnTable } from 'arquero';

interface IAccountGroupProps {
  section: AccountSection;
  accounts: FinancialAccount[];
  transactions?: ColumnTable | null;
  /**
   * Each account's value a month ago, by account id; null or absent when it had none. Feeds the
   * investments section's "vs. last month", which compares only the accounts that have one.
   */
  pastBalances?: ReadonlyMap<string, number | null>;
  onAccountClick?: (accountId: string) => void;
  /** Gives every row a "Hide account…" menu, on the non-compact layout only: a narrow row has no room for it. */
  onHideAccount?: (account: FinancialAccount) => void;
  /** Force compact layout for narrow containers like sidebars */
  compact?: boolean;
  /** Render as a flat section inside a parent card (no bg, shadow, or rounding). */
  flat?: boolean;
  /** Show a top border separator (for 2nd+ groups inside a parent card). */
  showTopBorder?: boolean;
}

/**
 * One collapsible section of the accounts list: a header with the section name, a change
 * indicator, and the summed balance, over a row per account. An account counts toward the sum
 * once it has synced (`accountAsOf`). The change indicator shows on the non-compact desktop
 * layout only — derived from transactions for checking and savings, from `pastBalances` for
 * investments.
 */
const AccountGroup = ({
  section,
  accounts,
  transactions,
  pastBalances,
  onAccountClick,
  onHideAccount,
  compact = false,
  flat = false,
  showTopBorder = false,
}: IAccountGroupProps) => {
  const { isDesktop } = useDeviceBreakpoints();
  const useCompactLayout = compact || !isDesktop;

  if (accounts.length === 0) {
    return null;
  }

  // Check if all accounts in this section are syncing
  const allAccountsSyncing = accounts.every((account) => accountAsOf(account) === null);

  // Calculate sum of only synced account balances
  const balanceSum = accounts.reduce((acc, account) => {
    if (accountAsOf(account) !== null && account.currentBalance?.balance) {
      return acc + parseFloat(account.currentBalance.balance);
    }
    return acc;
  }, 0);

  const totalBalance = allAccountsSyncing ? null : (
    <span
      className={cx(
        'text-lg font-semibold',
        sectionTotalTone(section) === 'error' ? 'text-error-primary' : 'text-success-primary'
      )}
    >
      {toCurrency(balanceSum)}
    </span>
  );

  // Hide change indicator on mobile/compact layouts to match mockup
  const showChangeIndicator = !useCompactLayout && !allAccountsSyncing;
  let changeIndicator: React.ReactNode = null;
  if (showChangeIndicator && (section === 'checking' || section === 'savings')) {
    changeIndicator = (
      <BalanceChangeIndicator
        transactions={transactions ?? null}
        currentBalance={balanceSum}
        dateRange={getPresetDateRange('thisYear')}
      />
    );
  } else if (showChangeIndicator && section === 'investments') {
    const totals = likeForLikeTotals(
      accounts.map((account) => ({
        current: account.currentBalance ? parseFloat(account.currentBalance.balance) : null,
        past: pastBalances?.get(account.id) ?? null,
      }))
    );
    changeIndicator = totals && (
      <ChangeIndicator current={totals.current} previous={totals.past} versusText="vs. last month" increaseGood />
    );
  }

  const title = (
    <div className="flex items-center gap-2">
      <span>{sectionDisplayName(section)}</span>
      {changeIndicator}
    </div>
  );

  return (
    <Widget collapsible flat={flat} className={showTopBorder ? 'border-tertiary border-t' : undefined}>
      <Widget.Header title={title} trailing={totalBalance} />
      <Widget.Body className={flat ? 'pb-2' : undefined}>
        {accounts.map((financialAccount) => (
          <AccountRow
            key={financialAccount.id}
            financialAccount={financialAccount}
            onClick={onAccountClick}
            compact={compact}
            onHide={onHideAccount && !useCompactLayout ? () => onHideAccount(financialAccount) : undefined}
          />
        ))}
      </Widget.Body>
    </Widget>
  );
};

export default AccountGroup;
